
import { mapGetters } from 'vuex';
import utils from '@/utils';
import api from '@/api/profile';
import { defineComponent } from 'vue';
import { IUser } from '@/types/models';
import { IElForm } from '@/types/element';

interface IForm {
  userName: string;
  avatarUrl: string;
  mobile: string;
  code: string;
  mobileVerified: boolean;
}

interface IState {
  uploadUrl: string;
  uploadFileList: Array<{name: string, url: string}>;
  isSmsEnabled: boolean;
  isUserVerifyMobile: boolean;
  user: IUser;
  form: IForm;
  mobileValidateRules?: Array<Record<string, unknown>>;
  countdown: number;
}

export default defineComponent({
  data() {
    return {
      uploadUrl: '',
      uploadFileList: [],
      isSmsEnabled: false,
      isUserVerifyMobile: false,
      user: {} as IUser,
      form: {
        userName: '',
        avatarUrl: '',
        mobile: '',
        code: '',
        mobileVerified: false,
      },
      mobileValidateRules: undefined,
      countdown: 0,
    } as IState;
  },
  methods: {
    apiGet() {
      api.get((res) => {
        this.isSmsEnabled = res.isSmsEnabled;
        this.isUserVerifyMobile = res.isUserVerifyMobile;
        this.user = res.user;
        this.form = {
          userName: this.user.userName,
          avatarUrl: this.user.avatarUrl,
          mobile: this.user.mobile,
          code: '',
          mobileVerified: this.user.mobileVerified,
        };

        if (this.isUserVerifyMobile) {
          this.mobileValidateRules = [
            { required: true, message: '请输入手机号码' },
            {
              validator: utils.validateMobile,
              message: '请输入有效的手机号码',
            },
          ];
        } else {
          this.mobileValidateRules = [
            {
              validator: utils.validateMobile,
              message: '请输入有效的手机号码',
            },
          ];
        }

        if (this.form.avatarUrl) {
          this.uploadFileList.push({
            name: 'avatar',
            url: this.form.avatarUrl,
          });
        }
      });
    },

    apiSendSms() {
      api.sendSms({
        mobile: this.form.mobile,
      }, (res) => {
        if (!res) return;

        utils.notifySuccess('验证码发送成功，10分钟内有效');
        this.countdown = 60;
        const interval = setInterval(() => {
          this.countdown -= 1;
          if (this.countdown <= 0) {
            clearInterval(interval);
          }
        }, 1000);
      });
    },

    apiVerifyMobile() {
      api.verifyMobile({
        mobile: this.form.mobile,
        code: this.form.code,
      }, (res) => {
        if (!res) return;
        this.form.mobileVerified = true;
        this.apiSubmit();
      });
    },

    apiSubmit() {
      api.submit({
        userName: this.form.userName,
        avatarUrl: this.form.avatarUrl,
        mobile: this.form.mobile,
        mobileVerified: this.form.mobileVerified,
      }, (res) => {
        if (!res.value) return;
        utils.success('资料修改成功！');
      });
    },

    isMobile(value: string) {
      return /^1[3-9][0-9]\d{8}$/.test(value);
    },

    btnSendSmsClick() {
      if (this.countdown > 0) return;
      if (!this.form.mobile) {
        utils.notifyError('手机号码不能为空');
        return;
      }
      if (!this.isMobile(this.form.mobile)) {
        utils.notifyError('请输入有效的手机号码');
        return;
      }

      this.apiSendSms();
    },

    btnSubmitClick() {
      (this.$refs.form as IElForm).validate((valid) => {
        if (valid) {
          if (this.isMobileCode) {
            this.apiVerifyMobile();
          } else {
            this.apiSubmit();
          }
        }
      });
    },

    uploadBefore(file: {name: string; size: number}) {
      const re = /(\.jpg|\.jpeg|\.bmp|\.gif|\.png|\.webp)$/i;
      if (!re.exec(file.name)) {
        utils.error('头像只能是图片格式，请选择有效的文件上传!');
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        utils.error('头像图片大小不能超过 10MB!');
        return false;
      }
      return true;
    },

    uploadProgress() {
      utils.loading(true);
    },

    uploadSuccess(res: {value: string}, file: unknown, fileList: Array<string>) {
      this.form.avatarUrl = res.value;
      utils.loading(false);
      if (fileList.length > 1) fileList.splice(0, 1);
    },

    uploadError(err: {message: string}) {
      utils.loading(false);
      const error = JSON.parse(err.message);
      utils.error(error.message);
    },

    uploadRemove() {
      this.form.avatarUrl = '';
    },
  },
  computed: {
    ...mapGetters(['token']),

    isMobileCode(): boolean {
      if (this.isUserVerifyMobile) {
        return !this.user.mobileVerified || this.user.mobile !== this.form.mobile;
      }
      if (this.isSmsEnabled) {
        return this.user.mobile !== this.form.mobile;
      }
      return false;
    },
  },
  created() {
    this.uploadUrl = api.getUploadUrl();
    this.apiGet();
  },
});
